import { useEffect, useState } from 'react'
import { dataManagerApi } from '../../../services/dataManagerApi'

interface ImportTarget {
  key: string
  label: string
  description?: string
  domain: string
  required_fields?: string[]
  optional_fields?: string[]
  defaults?: Record<string, unknown>
}

interface Step1Props {
  onSelect: (targetKey: string, targets: ImportTarget[]) => void
}

const COMING_SOON: { label: string; description: string; domain: string }[] = [
  { label: 'Marketing', description: 'Campaigns, channels, and marketing spend', domain: 'Marketing' },
  { label: 'Internal Communications', description: 'Internal memos and announcements', domain: 'Communications' },
]

function Step1_SelectTarget({ onSelect }: Step1Props) {
  const [targets, setTargets] = useState<ImportTarget[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    dataManagerApi
      .getImportTargets()
      .then((result: unknown) => {
        if (cancelled) return
        // API may return { targets: [...] } or just [...]
        const arr = Array.isArray(result) ? result : (result as { targets?: ImportTarget[] }).targets ?? []
        setTargets(arr as ImportTarget[])
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load import targets')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  if (loading) {
    return (
      <div className="iw-loading">
        <span className="iw-spinner" />
        Loading import targets...
      </div>
    )
  }

  if (error) {
    return <div className="iw-error">{error}</div>
  }

  return (
    <div>
      <div className="iw-step-title">Select Import Target</div>
      <div className="iw-step-subtitle">Choose what type of data you want to import.</div>
      <div className="iw-targets-grid">
        {targets.map((t) => (
          <div
            key={t.key}
            className="iw-target-card"
            onClick={() => onSelect(t.key, targets)}
          >
            <div className="iw-target-card-label">{t.label}</div>
            {t.description && (
              <div className="iw-target-card-desc">{t.description}</div>
            )}
            <span className="iw-target-card-badge">{t.domain}</span>
          </div>
        ))}
        {COMING_SOON.map((c) => (
          <div key={c.label} className="iw-target-card iw-target-card--disabled">
            <div className="iw-target-card-label">
              {c.label}
              <span className="iw-target-card-soon">Coming Soon</span>
            </div>
            <div className="iw-target-card-desc">{c.description}</div>
            <span className="iw-target-card-badge">{c.domain}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default Step1_SelectTarget
export type { ImportTarget }
